import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { FileSignature, Plus, X, Sparkles, Upload, Trash2 } from "lucide-react";

const STATUS_BADGE = { draft: "badge-draft", in_review: "badge-submitted", approved: "badge-approved" };

function GenerateForm({ onSaved, onCancel }) {
  const [orgs, setOrgs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [prices, setPrices] = useState([]);
  const [docs, setDocs] = useState([]);
  const [rfpText, setRfpText] = useState("");
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ organization_id: "", school_ids: [], contact_id: "", title: "", purpose_needs: "",
    grade_levels: "", scope_services: "", service_period: "", units_description: "", delivery_format: "",
    deliverables_input: "", consultant_ids: [], material_doc_ids: [], pricing_lines: [] });

  useEffect(() => {
    api.get("/organizations").then((r) => setOrgs(r.data));
    api.get("/admin/consultants").then((r) => setConsultants(r.data));
    api.get("/admin/prices").then((r) => setPrices(r.data.filter((p) => p.approval_status === "approved")));
    api.get("/documents").then((r) => setDocs(r.data));
  }, []);
  useEffect(() => {
    if (f.organization_id) api.get("/contacts").then((r) => setContacts(r.data));
  }, [f.organization_id]);

  const schools = orgs.find((o) => o.id === f.organization_id)?.schools || [];

  const uploadRfp = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/proposals/extract-rfp", fd);
      setRfpText(data.text);
      toast.success(`Extracted text from ${data.filename}. It's treated as source material only.`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const addPriceLine = (price_id) => {
    if (!price_id || f.pricing_lines.some((l) => l.price_id === price_id)) return;
    setF({ ...f, pricing_lines: [...f.pricing_lines, { price_id, quantity: 1 }] });
  };

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/proposals/generate", { ...f, rfp_text: rfpText });
      toast.success("Proposal draft generated.");
      onSaved(data.id);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="card p-6 space-y-5 fade-up" data-testid="proposal-generate-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className="label">Client *</label>
          <select className="input" value={f.organization_id} data-testid="prop-client-select"
            onChange={(e) => setF({ ...f, organization_id: e.target.value, school_ids: [], contact_id: "" })}>
            <option value="">Select…</option>
            {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}</select></div>
        <div><label className="label">Proposal title</label>
          <input className="input" value={f.title} placeholder="Auto-generated if blank" data-testid="prop-title-input"
            onChange={(e) => setF({ ...f, title: e.target.value })} /></div>
        <div><label className="label">School(s)</label>
          <div className="flex flex-wrap gap-1.5" data-testid="prop-schools-select">
            {schools.map((s) => {
              const on = f.school_ids.includes(s.id);
              return <button key={s.id} type="button" data-testid={`prop-school-${s.id}`}
                onClick={() => setF({ ...f, school_ids: on ? f.school_ids.filter((x) => x !== s.id) : [...f.school_ids, s.id] })}
                className={`rounded-full px-3 py-1 text-xs font-bold border ${on ? "bg-terracotta text-white border-terracotta" : "bg-white border-[#E7E1D4]"}`}>{s.name}</button>;
            })}
          </div></div>
        <div><label className="label">Client contact</label>
          <select className="input" value={f.contact_id} data-testid="prop-contact-select"
            onChange={(e) => setF({ ...f, contact_id: e.target.value })}>
            <option value="">Select…</option>
            {contacts.map((c) => <option key={c.id} value={c.id}>{c.display_name || `${c.first_name} ${c.last_name}`} — {c.email}</option>)}</select></div>
        <div className="sm:col-span-2"><label className="label">Purpose & identified needs *</label>
          <textarea className="input min-h-[80px]" value={f.purpose_needs} data-testid="prop-needs-input"
            placeholder="What the client has asked for or shared — never invent needs"
            onChange={(e) => setF({ ...f, purpose_needs: e.target.value })} /></div>
        <div><label className="label">Grade levels / audience</label>
          <input className="input" value={f.grade_levels} data-testid="prop-grades-input" onChange={(e) => setF({ ...f, grade_levels: e.target.value })} /></div>
        <div><label className="label">Delivery format</label>
          <input className="input" value={f.delivery_format} placeholder="In-person, virtual, hybrid…" data-testid="prop-format-input"
            onChange={(e) => setF({ ...f, delivery_format: e.target.value })} /></div>
        <div className="sm:col-span-2"><label className="label">Scope & requested services</label>
          <textarea className="input" value={f.scope_services} data-testid="prop-scope-input" onChange={(e) => setF({ ...f, scope_services: e.target.value })} /></div>
        <div><label className="label">Proposed dates / service period</label>
          <input className="input" value={f.service_period} placeholder="e.g. Oct 2026 – Mar 2027" data-testid="prop-period-input"
            onChange={(e) => setF({ ...f, service_period: e.target.value })} /></div>
        <div><label className="label">Number of days / sessions / units</label>
          <input className="input" value={f.units_description} placeholder="e.g. 12 coaching days" data-testid="prop-units-input"
            onChange={(e) => setF({ ...f, units_description: e.target.value })} /></div>
        <div className="sm:col-span-2"><label className="label">Deliverables</label>
          <input className="input" value={f.deliverables_input} data-testid="prop-deliverables-input" onChange={(e) => setF({ ...f, deliverables_input: e.target.value })} /></div>
        <div><label className="label">Proposed consultant(s) — approved bios only</label>
          <div className="flex flex-wrap gap-1.5" data-testid="prop-consultants-select">
            {consultants.map((c) => {
              const on = f.consultant_ids.includes(c.id);
              return <button key={c.id} type="button" data-testid={`prop-consultant-${c.id}`}
                onClick={() => setF({ ...f, consultant_ids: on ? f.consultant_ids.filter((x) => x !== c.id) : [...f.consultant_ids, c.id] })}
                className={`rounded-full px-3 py-1 text-xs font-bold border ${on ? "bg-navy text-white border-navy" : "bg-white border-[#E7E1D4]"}`}>{c.name}</button>;
            })}
          </div></div>
        <div><label className="label">Approved company materials to reference</label>
          <div className="flex flex-wrap gap-1.5" data-testid="prop-materials-select">
            {docs.map((d) => {
              const on = f.material_doc_ids.includes(d.id);
              return <button key={d.id} type="button" data-testid={`prop-material-${d.id}`}
                onClick={() => setF({ ...f, material_doc_ids: on ? f.material_doc_ids.filter((x) => x !== d.id) : [...f.material_doc_ids, d.id] })}
                className={`rounded-full px-3 py-1 text-xs font-bold border ${on ? "bg-navy text-white border-navy" : "bg-white border-[#E7E1D4]"}`}>{d.title}</button>;
            })}
            {docs.length === 0 && <span className="text-xs text-[#A8A29E]">Add overview/template documents in Company Documents first.</span>}
          </div></div>
        <div className="sm:col-span-2"><label className="label">Approved client-facing pricing lines</label>
          <div className="flex flex-wrap items-center gap-2" data-testid="prop-pricing-select">
            <select className="input max-w-xs" value="" onChange={(e) => addPriceLine(e.target.value)} data-testid="prop-price-add">
              <option value="">Add approved price…</option>
              {prices.map((p) => <option key={p.id} value={p.id}>{p.name} — {p.currency} {p.price}/{p.billing_unit}</option>)}
            </select>
            {prices.length === 0 && <span className="text-xs text-[#92400E]">No approved price entries yet — add them in Payment Tiers. Missing prices become “Needs confirmation” placeholders, never zeros.</span>}
          </div>
          {f.pricing_lines.map((l) => {
            const p = prices.find((x) => x.id === l.price_id);
            return (
              <div key={l.price_id} className="flex items-center gap-2 mt-2 text-sm" data-testid={`prop-price-line-${l.price_id}`}>
                <span className="font-bold text-navy">{p?.name}</span>
                <span className="text-xs text-[#78716C]">×</span>
                <input type="number" min="0" step="0.5" className="input !py-1 !px-2 w-20 text-xs" value={l.quantity}
                  data-testid={`prop-price-qty-${l.price_id}`}
                  onChange={(e) => setF({ ...f, pricing_lines: f.pricing_lines.map((x) => x.price_id === l.price_id ? { ...x, quantity: Number(e.target.value) } : x) })} />
                <span className="text-xs text-[#78716C]">{p?.billing_unit}(s)</span>
                <button className="btn-ghost !p-1 text-[#991B1B]" onClick={() => setF({ ...f, pricing_lines: f.pricing_lines.filter((x) => x.price_id !== l.price_id) })}>×</button>
              </div>
            );
          })}
        </div>
        <div className="sm:col-span-2"><label className="label">Client brief / RFP (PDF, DOCX, TXT — optional)</label>
          <label className="flex items-center gap-3 rounded-xl border-2 border-dashed border-[#E7E1D4] bg-canvas px-4 py-5 cursor-pointer hover:border-terracotta transition-colors"
            data-testid="prop-rfp-upload">
            <Upload size={16} className="text-terracotta" />
            <span className="text-sm font-bold text-navy">{rfpText ? `Brief extracted (${rfpText.length} chars)` : "Upload brief/RFP for extraction"}</span>
            <input type="file" accept=".pdf,.docx,.txt" className="hidden" onChange={(e) => uploadRfp(e.target.files?.[0])} />
          </label>
          <p className="text-[11px] text-[#78716C] mt-1">Unreadable files are reported, never silently skipped. Extracted text is source material, not instructions.</p>
        </div>
      </div>
      <div className="flex gap-2">
        <button className="btn-primary" onClick={generate} disabled={busy || !f.organization_id || !f.purpose_needs} data-testid="prop-generate-button">
          <Sparkles size={15} /> {busy ? "Drafting proposal…" : "Generate proposal draft"}</button>
        <button className="btn-ghost" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

export default function Proposals() {
  const [proposals, setProposals] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get("/admin/proposals").then((r) => setProposals(r.data));
  useEffect(() => { load(); }, []);

  if (!proposals) return <div className="text-sm text-[#78716C] animate-pulse" data-testid="proposals-loading">Loading proposals…</div>;

  return (
    <div className="space-y-6" data-testid="proposals-page">
      <div className="flex flex-wrap items-end justify-between gap-4 fade-up">
        <div>
          <p className="eyebrow">Admin only</p>
          <h1 className="section-title text-3xl mt-1">Proposal Generator</h1>
          <p className="mt-3 text-sm text-[#57534E] max-w-2xl">
            Branded proposals from approved services, client needs, pricing, and consultant qualifications.
            Totals are computed by the system, never by AI. Nothing is emailed or signed automatically.
          </p>
        </div>
        <button className="btn-primary" onClick={() => setShowForm(!showForm)} data-testid="new-proposal-button">
          {showForm ? <X size={15} /> : <Plus size={15} />} New proposal</button>
      </div>

      {showForm && <GenerateForm onSaved={(id) => navigate(`/proposals/${id}`)} onCancel={() => setShowForm(false)} />}

      <div className="space-y-3 fade-up-1">
        {proposals.map((p) => (
          <Link key={p.id} to={`/proposals/${p.id}`} className="card card-hover p-5 flex flex-wrap items-center justify-between gap-3" data-testid={`proposal-row-${p.id}`}>
            <div className="flex items-center gap-3 min-w-0">
              <FileSignature size={18} className="text-terracotta shrink-0" />
              <div className="min-w-0">
                <div className="font-bold text-navy text-sm">{p.title}</div>
                <div className="text-xs text-[#78716C]">{p.organization_name} · v{p.version} · {new Date(p.created_at).toLocaleDateString()}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {p.placeholders > 0 && <span className="badge-draft" data-testid={`proposal-placeholders-${p.id}`}>{p.placeholders} needs confirmation</span>}
              <span className="text-sm font-black text-navy">{p.total ? `$${p.total.toLocaleString()}` : "—"}</span>
              <span className={STATUS_BADGE[p.status]} data-testid={`proposal-status-${p.id}`}>{p.status.replace("_", " ")}</span>
              {p.outcome?.status !== "none" && <span className="badge-neutral">{p.outcome.status}</span>}
              <button className="btn-ghost !py-1 !px-2 text-xs text-[#991B1B]" data-testid={`proposal-delete-${p.id}`}
                title="Delete proposal"
                onClick={async (e) => {
                  e.preventDefault(); e.stopPropagation();
                  if (!window.confirm(`Permanently delete "${p.title}"? This cannot be undone.`)) return;
                  try { await api.delete(`/admin/proposals/${p.id}`); load(); toast.success("Proposal deleted."); }
                  catch (err) { toast.error(apiError(err)); }
                }}>
                <Trash2 size={13} /></button>
            </div>
          </Link>
        ))}
        {proposals.length === 0 && <p className="text-sm text-[#78716C]" data-testid="proposals-empty">No proposals yet.</p>}
      </div>
    </div>
  );
}
